import { useState } from 'react';

import { apiFetch } from '@/api/client';
import { partiesKey, type Party, type PartyDuplicate } from '@/api/masters';
import { customersKey, employeesKey, vendorsKey } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Notice } from '@/components/PageHeading';
import { useEscapeKey } from '@/components/shortcuts';
import { StatusBadge } from '@/components/StatusBadge';

import { describeRoles, mergeProblems, suggestKeeper } from './roles';

/**
 * Folding two records into the one person they always were (the party merge, phase 4).
 *
 * <p>
 * <strong>The operator picks which identity survives, and that is a real decision rather than a
 * formality.</strong> The surviving party keeps its own name, number and address, so choosing
 * between "Ramesh Solanki" and "Ramesh S" is choosing whose spelling the shop keeps. A default is
 * suggested — the one more records already point at — but it is only a default.
 * </p>
 *
 * <p>
 * What moves is the role rows: every customer, vendor and staff record on the absorbed identity is
 * re-pointed at the one being kept, so <strong>no booking, job or fitting is touched</strong>. That
 * is only possible because the identity lives in its own table; while the name and number sat on the
 * customer row, merging would have meant rewriting every table that pointed at either.
 * </p>
 *
 * <p>
 * Hand-rolled rather than a `FormDrawer` because there is nothing to fill in — the whole interaction
 * is picking one of two cards — and a form footer would imply a form.
 * </p>
 */
export function MergeDrawer({
  duplicate,
  onClose,
}: {
  duplicate: PartyDuplicate;
  onClose: () => void;
}) {
  const [keepId, setKeepId] = useState<number | undefined>(
    () => suggestKeeper(duplicate.parties)?.id,
  );

  useEscapeKey(onClose, true);
  const fields = useAutoFocusFirstField<HTMLDivElement>(true);

  const merge = useApiMutation<Party, { keep: number; absorb: number }>({
    mutationFn: ({ keep, absorb }) =>
      apiFetch<Party>(`/api/party/${keep}/merge`, {
        method: 'POST',
        body: { fromPartyId: absorb },
      }),
    success: 'Records merged.',

    // Every master's list shows a name that now comes from the surviving identity, so all four
    // caches go — a merged-away party left in one of them is a row pointing at nothing.
    invalidates: [partiesKey, customersKey, vendorsKey, employeesKey],
    onSuccess: onClose,
  });

  const keep = duplicate.parties.find((party) => party.id === keepId);

  // Exactly two is the ordinary case and the only one this drawer commits in a single step. Three
  // identities on one number is rare, real, and handled by merging twice — which is what the
  // remaining pair shows after the first.
  const absorb = duplicate.parties.find((party) => party.id !== keepId);

  const problems = keep && absorb ? mergeProblems(keep, absorb) : [];
  const tooMany = duplicate.parties.length > 2;

  return (
    <div data-shortcut-layer className="fixed inset-0 z-9999 flex print:hidden">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 cursor-default bg-[var(--overlay-scrim)]"
      />

      <aside
        {...enterKeyScope}
        className="flex w-full max-w-[560px] flex-col border-s border-[var(--overlay-border)] bg-[var(--overlay-surface)]"
      >
        <header className="border-border-color flex items-center justify-between border-b px-5 py-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">Same person?</p>
            <p className="text-default font-mono-nums truncate text-[12px]">{duplicate.mobile}</p>
          </div>
          <Button icon="icon icon-x" onClick={onClose} disabled={merge.isPending}>
            Close
          </Button>
        </header>

        <div ref={fields} className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          <p className="text-default text-[13px]">
            These records share a number. Keep the one whose details are right — the other&apos;s
            customer, vendor and staff records move across, and nothing they are named on changes.
          </p>

          <div>
            <span className="mb-1.5 block text-[12px] font-medium text-gray-900">Keep</span>

            <div className="flex flex-col gap-2">
              {duplicate.parties.map((party) => (
                <button
                  key={party.id}
                  type="button"
                  disabled={merge.isPending}
                  onClick={() => setKeepId(party.id)}
                  aria-pressed={party.id === keepId}
                  className={`cursor-pointer rounded border px-3 py-2 text-left ${
                    party.id === keepId
                      ? 'border-primary bg-primary-transparent'
                      : 'border-border-color hover:bg-light bg-white'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-gray-900">{party.name}</span>
                    <StatusBadge tone={party.roles.length > 0 ? 'neutral' : 'warning'}>
                      {describeRoles(party.roles)}
                    </StatusBadge>
                  </span>
                  <span className="text-default font-mono-nums mt-0.5 block text-[12px]">
                    {party.mobile}
                    {party.city ? ` · ${party.city}` : ''}
                    {party.email ? ` · ${party.email}` : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {tooMany && (
            <Notice tone="info" title="Three or more on one number">
              Merge two at a time. Whichever you keep will still be listed with the rest until only
              one is left.
            </Notice>
          )}

          {problems.map((problem) => (
            <Notice key={problem} tone="warning" title="These two cannot be merged">
              {problem}
            </Notice>
          ))}

          <Notice tone="warning" title="There is no undo">
            The record you do not keep is retired. Its name and address go with it — only the
            customer, vendor and staff records move.
          </Notice>
        </div>

        <footer className="border-border-color flex justify-end gap-2 border-t bg-white px-5 py-3">
          <Button onClick={onClose} disabled={merge.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            data-enter-key-commit
            shortcut="save"
            disabled={merge.isPending || !keep || !absorb || problems.length > 0}
            onClick={() => {
              if (keep && absorb) {
                merge.mutate({ keep: keep.id, absorb: absorb.id });
              }
            }}
          >
            {keep ? `Keep ${keep.name}` : 'Merge'}
          </Button>
        </footer>
      </aside>
    </div>
  );
}
